<template>
  <div>
  <!-- Desktop -->
  <v-card
    v-if="!mobile"
    class="tarea-card mb-2"
    style="overflow: hidden;"
    @click.stop="$emit('view', entrega)"
  >
    <v-card-text
      class="row-card-content py-2"
      :class="entrega.status_info.status === 3 || entrega.status_info.status === 4 ? 'status-ended' : ''"
    >
      <v-row align="center" no-gutters>

        <!-- Col 1: Indicadores (id, prioridad, notificación) -->
        <v-col cols="1">
          <div class="d-flex flex-row icon-container flex-wrap">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="icon-top number d-flex justify-center align-center" v-bind="attrs" v-on="on">
                  <p class="mb-0 white--text text-task-number caption">{{ entrega.number }}</p>
                </div>
              </template>
              <span>Entrega #{{ entrega.number }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="white"
                  class="icon-top"
                  :class="priorityColor"
                  v-bind="attrs"
                  v-on="on"
                  small
                >mdi-exclamation</v-icon>
              </template>
              <span>Prioridad {{ priorityText }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="icon-top d-flex justify-center align-center"
                  :class="seenColor"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="$emit('change-visualization', entrega)"
                >
                  <v-icon
                    color="white"
                    :class="seenAnimation"
                    v-if="!isLoadingVisualization"
                    small
                  >mdi-bell</v-icon>
                  <v-progress-circular v-else :width="2" :size="16" color="white" indeterminate />
                </div>
              </template>
              <span>{{ seenText }}</span>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Col 2: Entrega + Ubicación + Documentos -->
        <v-col cols="4" class="pl-2">
          <div class="d-flex flex-column">
            <!-- Título y protocolo -->
            <p class="mb-0 body-2 font-weight-medium entrega-title">{{ entrega.title }}</p>
            <p class="mb-1 caption text--secondary">{{ entrega.protocolo_name }}</p>

            <!-- Especialidades -->
            <div class="entrega-location d-flex align-center flex-wrap mb-1" style="gap: 4px;">
							<span class="caption font-weight-medium">Especialidades:</span>
              <span v-if="entrega.specialties?.length" class="caption text--secondary location-tag">
                {{ entrega.specialties.map(s => s.specialty.name).join(', ') }}
              </span>
              <span v-else class="caption text--secondary location-tag">
                Sin Especialidades
              </span>
            </div>

            <!-- Ubicación -->
            <div class="entrega-location d-flex align-center flex-wrap mb-1" style="gap: 4px;">
							<span class="caption font-weight-medium">Ejes:</span>
              <span v-if="entrega.ejes_alfabeticos" class="caption text--secondary location-tag">
                {{ entrega.ejes_alfabeticos }}<template v-if="entrega.ejes_alfabeticos_b">-{{ entrega.ejes_alfabeticos_b }}</template>
              </span>
							/
              <span v-if="entrega.ejes_numericos" class="caption text--secondary location-tag">
                {{ entrega.ejes_numericos }}<template v-if="entrega.ejes_numericos_b">-{{ entrega.ejes_numericos_b }}</template>
              </span>
							|
              <span v-if="entrega.nivel" class="caption text--secondary location-tag">
                <span class="font-weight-medium">Niv:</span> {{ entrega.nivel }}
              </span>
							|
              <span v-if="zonaName" class="caption text--secondary location-tag">
                <span class="font-weight-medium">Zona:</span> {{ zonaName }}
              </span>
            </div>

            <!-- Documentos asociados (antes columna separada) -->
            <div class="d-flex flex-column entrega-docs" v-if="hasDocuments">
              <v-divider class="mb-1"></v-divider>
              <div class="d-flex align-center doc-row" v-if="entrega.plano && entrega.plano.title">
                <v-icon x-small class="mr-1 flex-shrink-0" color="grey">mdi-map-outline</v-icon>
                <span class="caption text--secondary doc-text">{{ entrega.plano.title }}</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Col 3: Badges de Aprobación (expandida de 3 → 4 cols) -->
        <v-col cols="4" class="px-2">
          <div class="entrega-badges d-flex flex-wrap">
            <v-menu
              v-for="(subject, idx) in activeAprobadores"
              :key="idx"
              max-width="340"
              :close-on-content-click="false"
              open-on-hover
              offset-y
              top
              content-class="entrega-approval-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="entrega-badge-activator" v-bind="attrs" v-on="on" @click.stop>
                  <!-- Main badge (permite aprobar) -->
                  <div class="entrega-badge">
                    <template v-if="canCurrentUserApproveSubject(subject)">
                      <v-icon :color="'#9CA3AF'" size="18">mdi-circle-outline</v-icon>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </template>
                    <template v-else>
                      <v-icon :color="getSubjectColor(subject)" size="18">
                        {{ getSubjectIcon(subject) }}
                      </v-icon>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </template>
                  </div>

                  <!-- Badge adicional para aprobaciones de OTROS usuarios con el mismo perfil -->
                  <div v-if="canCurrentUserApproveSubject(subject) && getSubjectOtherApprovalsCount(subject) > 0" class="entrega-badge-others-wrapper">
                    <div class="entrega-badge" @click.stop>
                      <v-icon :color="getSubjectColor(subject)" size="18">{{ getSubjectIcon(subject) }}</v-icon>
                      <div v-if="getSubjectOtherApprovalsCount(subject) > approvalBadgeCountThreshold" class="entrega-badge-side-count">
                        x{{ getSubjectOtherApprovalsCount(subject) }}
                      </div>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <v-card flat class="approval-menu-card pa-2">
								<!-- Header con contador y dot parcial -->
								<div class="approval-menu-header">
									<div class="approval-menu-header-top">
										<strong class="approval-menu-title">{{ subject.name }}</strong>
										<span class="approval-menu-status">{{ getSubjectStatusText(subject) }}</span>
									</div>
									<span class="approval-menu-counter">
                    <span
                      v-if="_subjectStats(subject).partial > 0 || (_subjectStats(subject).approved > 0 && _subjectStats(subject).approved < _subjectStats(subject).total)"
                      class="approval-partial-dot"
                    />
                    <v-icon :color="getSubjectColor(subject)" size="13">{{ getSubjectIcon(subject) }}</v-icon>
                    {{ _subjectStats(subject).approved }}/{{ _subjectStats(subject).total }} tareas · mín. {{ _subjectStats(subject).minRequired }}
                    <span style="margin-left:6px; color:#6B7280; font-size:10px;">
                      · disponibles {{ _subjectStats(subject).open }}/{{ _subjectStats(subject).total }} (máx/tarea {{ _subjectStats(subject).maxAllowed }})
                    </span>
                  </span>
								</div>

								<!-- Filas de aprobación -->
								<template v-if="getSubjectApprovalDetails(subject).length">
									<div
										v-for="(detail, di) in getSubjectApprovalDetails(subject)"
										:key="di"
										class="approval-group-item"
										@click.stop="toggleGroup('d-' + idx + '-' + di)"
									>
										<div class="approval-group-header">
											<v-icon x-small class="mr-1 flex-shrink-0" color="#2D4B95">
												{{ expandedGroups['d-' + idx + '-' + di] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
											</v-icon>
											<!-- Aprobador -->
											<span v-if="detail.approver_name" class="approval-group-approver">
												{{ detail.approver_name }}
											</span>
											<span :style="{ color: getEstadoTooltipColor(detail.estado) }" class="approval-group-estado ml-1">
												{{ detail.estado }}
											</span>
											<span v-if="detail.comentario" class="approval-group-comment ml-1">
												&ldquo;{{ detail.comentario }}&rdquo;
											</span>
										</div>
										<div v-if="!expandedGroups['d-' + idx + '-' + di]" class="approval-group-preview">
											{{ detail.punto_controles.join(' · ') }}
										</div>
										<div v-else class="approval-group-detail">
											<div
												v-for="(pc, pci) in detail.punto_controles"
												:key="pci"
												class="approval-group-pc"
											>· {{ pc }}</div>
										</div>
									</div>
								</template>
								<div v-else class="approval-tooltip-empty">Sin aprobaciones registradas</div>
							</v-card>
            </v-menu>
          </div>
        </v-col>

        <!-- Col 4: Estado + Supervisor + Fechas -->
        <v-col cols="2" class="pl-1">
          <v-chip x-small :color="entrega.status_info.color" class="status-chip mb-1 white--text">
            {{ entrega.status_info.text }}
          </v-chip>
          <p class="mb-0 caption text--secondary">
            {{ approvedTasksCount }}/{{ entrega.tasks.length }} tareas
          </p>
          <p class="mb-0 caption text--secondary text-truncate" v-if="supervisorName">
            <v-icon x-small color="grey" class="mr-1">mdi-account-hard-hat</v-icon>{{ supervisorName }}
          </p>
          <div class="mt-1">
            <v-tooltip bottom v-if="entrega.critical_date">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-flex align-center rounded px-1 mb-1 date-tag"
                  :class="isNearDeadline ? 'near-deadline' : ''"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small :color="isNearDeadline ? 'white' : '#2D4B95'" class="mr-1">
                    mdi-calendar-range-outline
                  </v-icon>
                  <span class="caption" :class="isNearDeadline ? 'white--text' : 'text--secondary'">
                    {{ entrega.critical_date | toDate }}
                  </span>
                </div>
              </template>
              <span>Fecha crítica: {{ entrega.critical_date | toDate }}</span>
            </v-tooltip>

            <v-tooltip bottom v-if="entrega.ended_at">
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex align-center rounded px-1 bg-normal date-tag" v-bind="attrs" v-on="on">
                  <v-icon x-small color="white" class="mr-1">mdi-calendar-check-outline</v-icon>
                  <span class="caption white--text">{{ entrega.ended_at | toDate('YYYY-MM-DD HH:mm:ss') }}</span>
                </div>
              </template>
              <span>Fecha término: {{ entrega.ended_at | toDate('YYYY-MM-DD HH:mm:ss') }}</span>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Col 5: Acciones -->
        <v-col cols="1" class="text-right">
          <div class="d-flex flex-column align-end">
            <v-tooltip bottom v-if="!canEdit">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon disabled x-small v-bind="attrs" v-on="on" class="mb-1">
                  <v-icon color="grey lighten-1" small>mdi-calendar-edit</v-icon>
                </v-btn>
              </template>
              <span>Sin permisos para editar</span>
            </v-tooltip>
            <v-btn
              v-else
              icon
              x-small
              class="mb-1"
              @click.stop="$emit('edit', entrega)"
              title="Editar fecha crítica"
            >
              <v-icon color="grey darken-1" small>mdi-calendar-edit</v-icon>
            </v-btn>
          </div>
        </v-col>

      </v-row>
    </v-card-text>
  </v-card>

  <!-- Mobile -->
  <v-card
    v-else
    class="tarea-card mb-2"
    style="overflow: hidden;"
    @click.stop="$emit('view', entrega)"
  >
    <!-- Indicador privado -->
    <v-tooltip bottom v-if="entrega.private">
      <template v-slot:activator="{ on, attrs }">
        <div class="incognito-container" v-bind="attrs" v-on="on">
          <v-icon color="white" small>mdi-incognito</v-icon>
        </div>
      </template>
      <span>Entrega privada: sólo la puedes ver tú y el responsable</span>
    </v-tooltip>

    <v-card-text class="row-card-content py-2">
      <v-row align="center">

        <!-- Íconos + título + ubicación -->
        <v-col cols="4">
          <div class="d-flex flex-row icon-container">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="icon-top number d-flex justify-center align-center" v-bind="attrs" v-on="on">
                  <p class="mb-0 white--text text-task-number caption">{{ entrega.number }}</p>
                </div>
              </template>
              <span>Entrega número {{ entrega.number }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="white"
                  class="icon-top"
                  :class="priorityColor"
                  v-bind="attrs"
                  v-on="on"
                  small
                >mdi-exclamation</v-icon>
              </template>
              <span>Prioridad {{ priorityText }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="icon-top d-flex justify-center align-center"
                  :class="seenColor"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="$emit('change-visualization', entrega)"
                >
                  <v-icon
                    color="white"
                    :class="seenAnimation"
                    v-if="!isLoadingVisualization"
                    small
                  >mdi-bell</v-icon>
                  <v-progress-circular v-else :width="2" :size="16" color="white" indeterminate />
                </div>
              </template>
              <span>{{ seenText }}</span>
            </v-tooltip>
          </div>
        </v-col>

        <v-col cols="8">
          <p class="mb-0 text-subtitle-1 font-weight-bold">{{ entrega.title }}</p>
          <p class="mb-0 caption text--secondary">{{ entrega.protocolo_name }}</p>
          <div class="entrega-location d-flex flex-wrap">
            <span v-if="entrega.ejes_alfabeticos" class="caption text--secondary location-tag">
              <span class="font-weight-medium">Eje α:</span>
              {{ entrega.ejes_alfabeticos }}<template v-if="entrega.ejes_alfabeticos_b">-{{ entrega.ejes_alfabeticos_b }}</template>
            </span>
            <span v-if="entrega.ejes_numericos" class="caption text--secondary location-tag">
              <span class="font-weight-medium">Eje n:</span>
              {{ entrega.ejes_numericos }}<template v-if="entrega.ejes_numericos_b">-{{ entrega.ejes_numericos_b }}</template>
            </span>
            <span v-if="entrega.nivel" class="caption text--secondary location-tag">
              <span class="font-weight-medium">Nivs:</span> {{ entrega.nivel }}
            </span>
          </div>
        </v-col>

        <v-col cols="12" class="pa-0"><v-divider></v-divider></v-col>

        <!-- Aprobaciones (badges por subject) -->
        <v-col cols="6" class="px-1">
          <div class="d-flex flex-row flex-wrap pt-1">
            <v-menu
              v-for="(subject, si) in activeAprobadores"
              :key="'msub-' + si"
              v-model="mobileMenus[si]"
              max-width="280"
              :close-on-content-click="false"
              open-on-hover
              offset-y
              top
              content-class="entrega-approval-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="entrega-badge-activator" v-bind="attrs" v-on="on" @click.stop>
                  <div class="entrega-badge">
                    <template v-if="canCurrentUserApproveSubject(subject)">
                      <v-icon :color="'#9CA3AF'" size="16">mdi-circle-outline</v-icon>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </template>
                    <template v-else>
                      <v-icon :color="getSubjectColor(subject)" size="16">{{ getSubjectIcon(subject) }}</v-icon>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </template>
                  </div>

                  <div v-if="canCurrentUserApproveSubject(subject) && getSubjectOtherApprovalsCount(subject) > 0" class="entrega-badge-others-wrapper">
                    <div class="entrega-badge" @click.stop>
                      <v-icon :color="getSubjectColor(subject)" size="16">{{ getSubjectIcon(subject) }}</v-icon>
                      <div v-if="getSubjectOtherApprovalsCount(subject) > approvalBadgeCountThreshold" class="entrega-badge-side-count">
                        x{{ getSubjectOtherApprovalsCount(subject) }}
                      </div>
                      <span class="entrega-badge-label">{{ subject.name }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <v-card flat class="approval-menu-card pa-2">
                <div class="approval-menu-header">
                  <strong>{{ subject.name }}</strong> — {{ getSubjectStatusText(subject) }}
                  <v-btn icon x-small class="approval-menu-close" @click.stop="$set(mobileMenus, si, false)">
                    <v-icon size="14" color="grey">mdi-close</v-icon>
                  </v-btn>
                  <div style="color:#6B7280; font-size:11px; margin-top:6px;">
                    {{ _subjectStats(subject).approved }}/{{ _subjectStats(subject).total }} · disponibles {{ _subjectStats(subject).open }}/{{ _subjectStats(subject).total }} · máx/tarea {{ _subjectStats(subject).maxAllowed }}
                  </div>
                </div>
                <template v-if="getSubjectApprovalDetails(subject).length">
                  <div
                    v-for="(detail, di) in getSubjectApprovalDetails(subject)"
                    :key="di"
                    class="approval-group-item"
                    @click.stop="toggleGroup('m-' + si + '-' + di)"
                  >
                    <div class="approval-group-header">
                      <v-icon x-small class="mr-1 flex-shrink-0" color="#2D4B95">
                        {{ expandedGroups['m-' + si + '-' + di] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                      <span :style="{ color: getEstadoTooltipColor(detail.estado) }" class="approval-group-estado">{{ detail.estado }}</span>
                      <span class="approval-group-comment ml-1">&ldquo;{{ detail.comentario }}&rdquo;</span>
                    </div>
                    <div v-if="!expandedGroups['m-' + si + '-' + di]" class="approval-group-preview">
                      {{ detail.punto_controles.join(' · ') }}
                    </div>
                    <div v-else class="approval-group-detail">
                      <div
                        v-for="(pc, pci) in detail.punto_controles"
                        :key="pci"
                        class="approval-group-pc"
                      >· {{ pc }}</div>
                    </div>
                  </div>
                </template>
                <div v-else class="approval-tooltip-empty">Sin observaciones</div>
              </v-card>
            </v-menu>
          </div>
        </v-col>

        <v-divider vertical></v-divider>

        <!-- Estado + supervisor + fechas -->
        <v-col cols="5" class="px-1">
          <div class="d-flex flex-row align-center mb-1">
            <p class="mb-0 text-caption mr-1">Estado:</p>
            <v-chip x-small :color="entrega.status_info.color" text-color="white">
              {{ entrega.status_info.text }}
            </v-chip>
          </div>
          <div class="d-flex flex-column mb-1" v-if="supervisorName">
            <p class="mb-0 text-caption">Responsable:</p>
            <p class="mb-0 text-caption font-weight-bold">{{ supervisorName }}</p>
          </div>

          <!-- Finalizada -->
          <div v-if="entrega.status_info.status == 3" class="d-flex flex-row align-center">
            <v-icon color="#3EBFA3" small class="mr-1">mdi-alert-circle-check</v-icon>
            <p class="mb-0 font-weight-bold normal caption">Finalizada</p>
          </div>

          <!-- Fecha creación -->
          <div class="d-flex flex-row align-center mt-1">
            <v-icon x-small color="#2D4B95" class="mr-1">mdi-calendar-range-outline</v-icon>
            <p class="mb-0 text--secondary caption">
              <small>{{ entrega.created_at | toDate }} {{ entrega.created_at | toHour }}</small>
            </p>
          </div>

          <!-- Fecha crítica -->
          <v-tooltip bottom v-if="entrega.critical_date">
            <template v-slot:activator="{ on, attrs }">
              <div
                class="d-flex flex-row align-center mt-1 rounded px-1"
                :class="isNearDeadline ? 'near-deadline' : ''"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  small
                  :color="isNearDeadline ? 'white' : '#2D4B95'"
                  class="mr-1"
                >mdi-calendar-range-outline</v-icon>
                <p
                  class="mb-0 caption"
                  :class="isNearDeadline ? 'white--text' : 'text--secondary'"
                >
                  <small>{{ entrega.critical_date | toDate }}</small>
                </p>
              </div>
            </template>
            <span>Fecha crítica: {{ entrega.critical_date | toDate }}</span>
          </v-tooltip>

          <!-- Fecha término -->
          <v-tooltip bottom v-if="entrega.ended_at">
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex flex-row align-center mt-1 rounded px-1 bg-normal" v-bind="attrs" v-on="on">
                <v-icon small color="white" class="mr-1">mdi-calendar-range-outline</v-icon>
                <p class="mb-0 caption white--text">
                  {{ entrega.ended_at | toDate('YYYY-MM-DD HH:mm:ss') }}
                </p>
              </div>
            </template>
            <span>Fecha término: {{ entrega.ended_at | toDate('YYYY-MM-DD HH:mm:ss') }}</span>
          </v-tooltip>
        </v-col>

      </v-row>
    </v-card-text>
  </v-card>
  </div>
</template>

<script>
import moment from 'moment';
import { checkPermissions } from '@/helpers/permissions';

export default {
  name: 'EntregaRow',

  props: {
    entrega: {
      type: Object,
      required: true,
    },
    activeAprobadores: {
      type: Array,
      default: () => [],
    },
    planos: {
      type: Array,
      default: () => [],
    },
    configData: {
      type: Object,
      default: () => ({ minimo: 0, aprobadores: [] }),
    },
    zonasList: {
      type: Array,
      default: () => [],
    },
    isLoadingVisualization: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['view', 'edit', 'change-visualization'],

  data() {
    return {
      expandedGroups: {},
      mobileMenus: {},
      approvalBadgeCountThreshold: 5,
    };
  },

  computed: {
    // ── Priority ──────────────────────────────────────────────────────────────
    priorityColor() {
      const p = this.entrega.priority;
      if (p === 3) return 'high';
      if (p === 2) return 'medium';
      return 'normal';
    },
    priorityText() {
      const p = this.entrega.priority;
      if (p === 3) return 'Inmediata';
      if (p === 2) return 'Media';
      return 'Estándar';
    },

    // ── Seen ──────────────────────────────────────────────────────────────────
    seenColor() {
      return Number(this.entrega.seen_by_me?.seen_status ?? this.entrega.seen_status ?? 0) === 0 ? 'high' : 'normal';
    },
    seenAnimation() {
      return Number(this.entrega.seen_by_me?.seen_status ?? this.entrega.seen_status ?? 0) === 0 ? 'pulse' : '';
    },
    seenText() {
      return Number(this.entrega.seen_by_me?.seen_status ?? this.entrega.seen_status ?? 0) === 0 ? 'No leído' : 'Leído';
    },

    // ── Location ──────────────────────────────────────────────────────────────
    zonaName() {
      const zona = this.zonasList.find(z => z.id === this.entrega.zona_id);
      return zona ? zona.name : '';
    },

    // ── Documents ─────────────────────────────────────────────────────────────
    hasDocuments() {
      const e = this.entrega;
      return (e.plano && e.plano.title) || e.tarea || (e.rdi_ref && e.rdi_ref.title) || e.adicional;
    },

    // ── Plano data for PlanoViewer ────────────────────────────────────────────
    planoData() {
      return this.entrega.plano_entrega || null;
    },

    // ── Status ────────────────────────────────────────────────────────────────
		approvedTasksCount() {
			return (this.entrega.tasks || []).filter(t => this._taskMeetsMinimum(t)).length;
		},

    // ── Supervisor ────────────────────────────────────────────────────────────
    supervisorName() {
      if (!this.entrega.supervisor_id) return null;
      const user = this.$store.usersStore?.state?.usersList?.find(
        u => u.id === this.entrega.supervisor_id
      );
      return user ? user.name : null;
    },

    // ── Deadline ──────────────────────────────────────────────────────────────
    isNearDeadline() {
      if (!this.entrega.critical_date) return false;
      return moment(this.entrega.critical_date).diff(moment(), 'days') <= 2;
    },

    // ── Permissions ───────────────────────────────────────────────────────────
    canEdit() {
      return checkPermissions(['edit_entrega']);
    },
  },

  filters: {
    toDate(date, format) {
      if (!date) return '';
      return moment(date).format(format || 'DD/MM/YYYY');
    },
    toHour(date) {
      if (!date) return '';
      return moment(date).format('HH:mm');
    },
  },

  methods: {
    // ── Approval badge helpers ────────────────────────────────────────────────
		_taskMeetsMinimum(task) {
			if (task.autocontrol) return true;
			if (!task.approvals || task.approvals.length === 0) return false;

			const aprobadores = this.configData?.aprobadores || [];
			const minimoGlobal = this.configData?.minimo || 0;

			// Validar mínimo global: cantidad de subjects distintos con al menos 1 aprobación válida
			const subjectsAprobados = new Set(
				task.approvals
					.filter(a => a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones')
					.map(a => a.subject_id)
			);
			if (subjectsAprobados.size < minimoGlobal) return false;

			// Si hay config por aprobador, validar el mínimo de cada uno con min > 0
			if (aprobadores.length) {
				const required = aprobadores.filter(ap => ap.min > 0);
				return required.every(ap => this._countApprovedForSubject(task, ap.id) >= ap.min);
			}

			return true;
		},
		/**
		 * Retorna config del aprobador para un subject dado.
		 * Fallback: min=1 si no está definido.
		 */
		_getAprobadorConfig(subjectId) {
			const aprobadores = this.configData?.aprobadores || [];
			return aprobadores.find(a => a.id === subjectId) || { min: 1, max: 1 };
		},

		/**
		 * Para una task y subject, cuenta cuántas aprobaciones
		 * tienen estado 'Aprobado' o 'Aprobado con Observaciones'.
     */
    _countApprovedForSubject(task, subjectId) {
      const list = (task.approvals || []).filter(a =>
        (subjectId === null ? a.subject_id === null : a.subject_id == subjectId) &&
        (a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones')
      );
      // Contar aprobaciones por usuario único (deduplicar por user_id)
      const users = new Set(
        list.map(a => (a.user_id !== undefined ? a.user_id : (a.user?.id || null)))
      );
      users.delete(null);
      return users.size;
    },

    /**
     * Cuenta usuarios únicos (EXCLUYENDO al usuario actual) que han aprobado
     * para un `subject` en cualquiera de las tareas de esta entrega.
     */
    _countOtherApproversForSubject(subject) {
      const userId = this.$store.userStore?.state?.userInfo?.info?.id || null;
      const ids = new Set();
      (this.entrega.tasks || []).forEach(task => {
        (task.approvals || []).forEach(a => {
          const subjectMatch = (subject?.id === null) ? a.subject_id === null : a.subject_id == subject.id;
          const estadoOk = a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones';
          if (subjectMatch && estadoOk) {
            const uid = (a.user_id !== undefined ? a.user_id : (a.user?.id || null));
            if (uid !== null && uid !== userId) ids.add(uid);
          }
        });
      });
      return ids.size;
    },

    getSubjectOtherApprovalsCount(subject) {
      return this._countOtherApproversForSubject(subject);
    },

		_subjectStats(subject) {
			const tasks = this.entrega.tasks || [];
			const total = tasks.length;
      const { min, max } = this._getAprobadorConfig(subject.id);
      const minRequired = min || 1;
      const maxAllowed = max || 1;

			// Una task está "aprobada" para este subject si alcanzó el mínimo
			const approved = tasks.filter(t =>
				this._countApprovedForSubject(t, subject.id) >= minRequired
			).length;

			// Parcial: tiene al menos 1 aprobado pero no llega al mínimo
			const partial = tasks.filter(t => {
				const count = this._countApprovedForSubject(t, subject.id);
				return count > 0 && count < minRequired;
			}).length;

			// Observado/Rechazado: al menos 1 en ese estado en las approvals del subject
			const observed = tasks.filter(t =>
				(t.approvals || []).some(a => a.subject_id == subject.id && a.estado === 'Observado')
			).length;

			const rejected = tasks.filter(t =>
				(t.approvals || []).some(a => a.subject_id == subject.id && a.estado === 'Rechazado')
			).length;

			const any = tasks.filter(t =>
				(t.approvals || []).some(a => a.subject_id == subject.id)
			).length;

      // Contar cuántas tareas ya alcanzaron el máximo de aprobaciones para este subject
      const full = tasks.filter(t => {
        const count = this._countApprovedForSubject(t, subject.id);
        return count >= maxAllowed;
      }).length;

      const open = total - full; // tareas que aún pueden recibir aprobaciones

      return { approved, partial, observed, rejected, any, total, minRequired, maxAllowed, full, open };
		},

    getSubjectColor(subject) {
      const { approved, partial, observed, rejected, any, total, full } = this._subjectStats(subject);
      if (rejected > 0) return '#F4516C';
      // Si todas las tareas están aprobadas y además alcanzaron el max → verde oscuro
      if (approved >= total && total > 0 && full >= total) return '#2e7d32';
      // Si todas las tareas están aprobadas pero aún hay espacio para más → verde claro
      if (approved >= total && total > 0) return '#3EBFA3';
      if (partial > 0) return '#FFC107'; // parcial: amarillo
      if (observed > 0) return '#FFC107';
      if (any > 0) return 'orange';
      return '#9CA3AF';
    },

    getSubjectIcon(subject) {
      const { approved, partial, observed, rejected, any, total, full } = this._subjectStats(subject);
      if (rejected > 0) return 'mdi-close-circle-outline';
      if (approved >= total && total > 0 && full >= total) return 'mdi-check-circle-outline';
      if (approved >= total && total > 0) return 'mdi-check-circle';
      if (partial > 0 || observed > 0 || any > 0) return 'mdi-circle-half-full';
      return 'mdi-circle-outline';
    },

    getSubjectStatusText(subject) {
      const { approved, partial, observed, rejected, any, total, minRequired, full, open } = this._subjectStats(subject);
      if (rejected > 0) return `Rechazado (${rejected} tarea${rejected > 1 ? 's' : ''})`;
      if (approved >= total && total > 0) {
        if (full >= total) return 'Aprobado completo (máx alcanzado)';
        return `Aprobado (${open} tarea${open > 1 ? 's' : ''} con espaci para aprobación)`;
      }
      if (partial > 0) return `Parcial — faltan aprobaciones (mín. ${minRequired})`;
      if (observed > 0) return `Observado (${observed} tarea${observed > 1 ? 's' : ''})`;
      if (any > 0) return `En revisión (${any}/${total})`;
      return 'Pendiente';
    },
		
		getSubjectApprovalDetails(subject) {
			const items = [];

			(this.entrega.tasks || []).forEach(task => {
				const list = (task.approvals || [])
					.filter(a => a.subject_id == subject.id)
					.sort((a, b) => b.id - a.id);

				list.forEach(aprov => {
					items.push({
						punto_control: task.punto_control,
						estado: aprov.estado,
						comentario: aprov.comentario || '',
						approver_name: aprov.user?.name || null,
					});
				});
			});

			// Agrupar por estado + comentario + aprobador
			const groups = [];
			items.forEach(item => {
				const existing = groups.find(
					g => g.estado === item.estado &&
							g.comentario === item.comentario &&
							g.approver_name === item.approver_name
				);
				if (existing) {
					existing.punto_controles.push(item.punto_control);
				} else {
					groups.push({
						punto_controles: [item.punto_control],
						estado: item.estado,
						comentario: item.comentario,
						approver_name: item.approver_name,
					});
				}
			});
			return groups;
		},

    getEstadoTooltipColor(estado) {
      const colors = {
        'Aprobado': '#2e7d32',
        'Observado': '#e65100',
        'Rechazado': '#c62828',
        'Aprobado con Observaciones': '#2D4B95',
      };
      return colors[estado] || '#9CA3AF';
    },

    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },
    /**
     * Devuelve true si el usuario actual puede aprobar al menos
     * una tarea para el `subject` en esta entrega.
     */
    canCurrentUserApproveSubject(subject) {
      const subjectId = subject?.id ?? null;
      const userId = this.$store.userStore?.state?.userInfo?.info?.id || null;
      if (!userId) return false;

      // Comprobar si el usuario tiene el role/job correspondiente al subject
      const projectJobId = this.$store.projectsStore?.state?.selectedProject?.job_type?.id || null;
      const isUserJob = subjectId !== null ? projectJobId === subjectId : false;
      if (!isUserJob) return false;

      const tasks = this.entrega.tasks || [];
      const { maxAllowed } = this._subjectStats(subject);
      const limit = maxAllowed || 1;

      for (const t of tasks) {
        if (t.autocontrol) continue;
        const approvedCount = this._countApprovedForSubject(t, subjectId);
        // si hay espacio para más aprobaciones en esta tarea
        if (approvedCount < limit) {
          // revisar si el usuario ya aprobó esta tarea
          const userAlready = (t.approvals || []).some(a =>
            (a.user_id !== undefined ? a.user_id : (a.user?.id || null)) === userId &&
            (a.estado === 'Aprobado' || a.estado === 'Aprobado con Observaciones') &&
            (subjectId === null ? a.subject_id === null : a.subject_id == subjectId)
          );
          if (!userAlready) return true;
        }
      }
      return false;
    }
  },
};
</script>

<style scoped>
/* ── Card ──────────────────────────────────────────────────────────────────── */
.tarea-card {
  position: relative;
  transition: transform 0.15s, box-shadow 0.15s;
}
.tarea-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08) !important;
}
.row-card-content {
  border: 1px solid white;
}
.status-ended {
  border: 1px solid #4caf50;
}

/* ── Priority / Bell icons ───────────────────────────────────────────────────*/
.icon-container { gap: 4px; }
.icon-top {
  padding: 2px;
  border-radius: 100%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-top.number  {
	background-color: #2D4B95;
	width: 35px;
	max-width: 35px;
	border-radius: 5px;
}
.icon-top.high    { background-color: #f4516c; }
.icon-top.medium  { background-color: #fb8c00; }
.icon-top.normal  { background-color: #3ebfa3; }
.text-task-number { font-size: 10px !important; }

/* ── Col 2: título, ubicación y docs ─────────────────────────────────────────*/
.entrega-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.location-tag {
  white-space: nowrap;
}
.entrega-docs { gap: 1px; }
.doc-row      { min-height: 18px; }
.doc-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ── Approval badges ─────────────────────────────────────────────────────────*/
.entrega-badges { gap: 2px; }
.entrega-badge {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 3px 3px; /* más espacio superior para el icono "flotante" */
  width: 60px;
}
.entrega-badge-activator {
  display: flex;
  gap: 6px;
  align-items: center;
}
.entrega-badge-others-wrapper { position: relative; }
.entrega-badge-side-count {
  position: absolute;
  top: 2px;
  left: 65%;
  transform: translateX(-50%);
  z-index: 4;
  background: #374151;
  color: #fff;
  border-radius: 6px;
  padding: 0 4px;
  font-size: 9px;
  line-height: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.entrega-badge-side {
  position: absolute;
  top: 6px; /* arriba del icono principal */
  left: 65%;
  transform: translateX(-50%);
  z-index: 4;
}
.entrega-badge-label {
  font-size: 8px;
  color: grey;
  margin-top: 1px;
  line-height: 1.1;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Status chip ─────────────────────────────────────────────────────────────*/
.status-chip {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.7rem !important;
}

/* ── Date tags ───────────────────────────────────────────────────────────────*/
.date-tag      { display: inline-flex!important; }
.near-deadline { background-color: #fb8c00; }
.bg-normal     { background-color: #3ebfa3; }

/* ── Pulse animation ─────────────────────────────────────────────────────────*/
.pulse { animation: pulse-anim 2s infinite; }
@keyframes pulse-anim {
  0%   { transform: scale(0.95); opacity: 0.7; }
  50%  { transform: scale(1.1);  opacity: 1;   }
  100% { transform: scale(0.95); opacity: 0.7; }
}

/* ── Approval menu ───────────────────────────────────────────────────────────*/
.entrega-approval-menu {
  border: 2px solid #2D4B95 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(45, 75, 149, 0.18) !important;
}
.approval-menu-card   { min-width: 220px; }
.approval-menu-header {
  font-size: 12px;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #E0E7FF;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.approval-menu-header strong { flex: 1; min-width: 0; }
.approval-menu-header-top {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.approval-menu-title {
  color: #2D4B95;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.approval-menu-status {
  font-size: 11px;
  font-weight: 400;
  color: #374151;
}
.approval-menu-counter {
  font-size: 10px;
  color: #6B7280;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
}
.approval-menu-close { margin-left: auto; flex-shrink: 0; }
.approval-group-item   { border-top: 1px solid #F3F4F6; }
.approval-group-header {
  display: flex;
  align-items: center;
  padding: 5px 2px;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
}
.approval-group-header:hover { background-color: #EEF2FF; }
.approval-group-estado  { font-size: 11px; font-weight: 700; flex-shrink: 0; }
.approval-group-comment {
  font-size: 11px;
  font-style: italic;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
.approval-group-preview {
  font-size: 11px;
  color: #9CA3AF;
  padding: 0 2px 4px 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.approval-group-detail { padding: 2px 0 4px 22px; }
.approval-group-pc     { font-size: 11px; color: #374151; line-height: 1.6; }
.approval-tooltip-empty { font-size: 11px; color: #9CA3AF; font-style: italic; }
.approval-partial-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #FFC107;
  border: 1px solid #fff;
  flex-shrink: 0;
}
.approval-group-approver {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}
</style>